import { MAX_TO } from '@weft/types';
import type { PriceTier } from '@weft/types';
import { extendInIndex,
  isNotNullOrUndefined,
  isNullOrUndefined,
  isSingleItem,
  newId,
  removeInIndex, withDefault } from '@weft/utils';

export function updateTiers(tiers: Partial<PriceTier>[], tierIndex: number, updatedTier: Omit<Partial<PriceTier>, 'id'>) {
  const updatedTiers = extendInIndex(tiers, tierIndex, updatedTier);

  return adjustTiers(updatedTiers);
}

export function addNewTier(tiers: Partial<PriceTier>[]) {
  if (isSingleItem(tiers)) {
    return [{
      ...tiers[0],
      to: 100,
    }, {
      from: 101,
      to: MAX_TO,
      id: newId(),
      price: 0,
      flatRate: 0,
    }];
  }

  const [firstTiers, beforeLastTier, lastTier] = getSplitTiers(tiers);

  const newTierFrom = beforeLastTier.to! + 1;
  const newTierTo = newTierFrom + 1;

  const newTier = buildNewTier(newTierFrom, newTierTo, lastTier.price);

  const updatedTiers = [
    ...firstTiers,
    beforeLastTier,
    newTier,
    lastTier,
  ];

  return adjustTiers(updatedTiers);
}

export function deleteFromTiers(tiers: Partial<PriceTier>[], tierIndex: number) {
  const tierToDelete = tiers[tierIndex];
  const tiersWithUpdatedNextTier = extendInIndex(tiers, tierIndex + 1, { from: tierToDelete.from });

  const updatedTiers = removeInIndex(tiersWithUpdatedNextTier, tierIndex);

  return adjustTiers(updatedTiers);
}

export function adjustTiers(tiers: Partial<PriceTier>[]) {
  if (tiers.length === 1) {
    return [{
      ...tiers[0],
      from: 1,
      to: MAX_TO,
    }];
  }

  return tiers.reduce<Partial<PriceTier>[]>((adjustedTiers, tier, tierIndex) => {
    const previousTier = adjustedTiers[tierIndex - 1];

    const isLastTier = tierIndex === tiers.length - 1;
    const adjustedTier = adjustTier(previousTier, tier, isLastTier);

    return [
      ...adjustedTiers,
      adjustedTier,
    ];
  }, []);
}

function adjustTier(previousTier: Partial<PriceTier> | undefined, currentTier: Partial<PriceTier>, isLastTier = false) {
  const from = isNullOrUndefined(previousTier) ? currentTier.from : adjustTierFromValue(previousTier, currentTier);
  const to = isLastTier ? MAX_TO : adjustTierToValue({
    ...currentTier,
    from,
  });

  return {
    ...currentTier,
    from,
    to,
  };
}

function adjustTierFromValue({ to: previousTo }: Partial<PriceTier>, { from }: Partial<PriceTier>) {
  const isInvalid = isNotNullOrUndefined(previousTo) && isNotNullOrUndefined(from) && from - 1 !== previousTo;

  return isInvalid ? previousTo + 1 : from;
}

function adjustTierToValue({ from, to }: Partial<PriceTier>) {
  const isInvalid = isNotNullOrUndefined(from) && isNotNullOrUndefined(to) && from >= to;

  return isInvalid ? from + 1 : to;
}

function getSplitTiers(tiers: Partial<PriceTier>[]): [Partial<PriceTier>[], Partial<PriceTier>, Partial<PriceTier>] {
  const firstTiers = tiers.slice(0, -2);
  const beforeLastTier = tiers[tiers.length - 2];
  const lastTier = {
    ...tiers[tiers.length - 1],
    to: MAX_TO,
  };

  return [firstTiers, beforeLastTier, lastTier];
}

function buildNewTier(from: number, to: number, lastPrice: number | undefined) : Partial<PriceTier> {
  return {
    from,
    to,
    price: withDefault(lastPrice, 0),
    flatRate: 0,
  };
}